/* istanbul ignore file */
/* eslint-disable functional/immutable-data */

import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { Platform, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useResponsive } from '@sg/garnish';

import { LocationSearchTypePicker } from '../../../LocationSearchTypePicker';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns `LocationSearchTypePicker` component render function to use it
 * as custom `headerTitle` component for React Navigation*
 */
export const useRenderLocationSearchTypePicker = (
  props: ComponentProps<typeof LocationSearchTypePicker>,
) => {
  const {
    locationSearchType,
    onLocationSearchTypeChange,
    isDisabled = false,
  } = props;

  const { currentBreakpoint } = useResponsive();

  // ─── Animated Values ─────────────────────────────────────────────────

  const headerContainerOpacity = useSharedValue(0);
  const headerContainerWidth = useSharedValue(0);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const setHeaderAnimatedStylesValues = useCallback(
    (event: LayoutChangeEvent) => {
      headerContainerWidth.value = event.nativeEvent.layout.width;
      headerContainerOpacity.value = 1;
    },
    [headerContainerOpacity, headerContainerWidth],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  // This hook returns a custom `headerTitle` component that renders `LocationSearchTypePicker`.
  //
  // The header title container in React Navigation has a `maxWidth` property to prevent title overflow.
  // Unfortunately, it causes layout issues in our situation because the content of this component is larger
  // than the specified `maxWidth`. Fortunately the issue only occurs on the Web platform.
  //
  // There is a corresponding `headerTitleContainerStyle` property, which in theory can be used to reset
  // the previously mentioned `maxWidth`, but as of this writing (10/22) it is not working (prop is not passed).
  //
  // That is why we employ the tried-and-true absolute position + transform approach to
  // bypass `maxWidth` and correctly place our custom header.

  const webAnimatedStyle = useAnimatedStyle(() => {
    return {
      position: 'absolute',
      left: '-50%',
      transform: [{ translateX: -(headerContainerWidth.value / 2) }],
      opacity: withTiming(headerContainerOpacity.value), // to hide component jumping on transform, we animate opacity
    };
  });
  const containerStyle = currentBreakpoint.isXS
    ? Platform.select({ web: webAnimatedStyle })
    : styles.locationsSearchTypeHeader;

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(
    () => (
      <Animated.View
        onLayout={setHeaderAnimatedStylesValues}
        style={containerStyle}
      >
        <LocationSearchTypePicker
          locationSearchType={locationSearchType}
          onLocationSearchTypeChange={onLocationSearchTypeChange}
          isDisabled={isDisabled}
        />
      </Animated.View>
    ),
    [
      containerStyle,
      isDisabled,
      locationSearchType,
      setHeaderAnimatedStylesValues,
      onLocationSearchTypeChange,
    ],
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  locationsSearchTypeHeader: {
    flex: 1,
    alignItems: 'center',
  },
});
